/*
 * עיצוב טקסט משימה בסימון פשוט (גם בגיליון וגם בעורך):
 *   - שורה שמתחילה ב-"- " (או • * –) = בולט
 *   **מודגש**   *נטוי*   __קו תחתון__   ~~קו חוצה~~   ^^קו מעל^^
 */

/** בלוק בטקסט משימה: פסקה (שורה אחת) או רשימת בולטים רצופה */
export type TextBlock = { type: 'p'; text: string } | { type: 'ul'; items: string[] };

// "-" / "*" / "–" דורשים רווח אחריהם (כדי לא לפגוע ב"-5" או "**מודגש**");
// סימני בולט גרפיים (• ● ○ ■ ▪ ◦ ·) — הרווח אחריהם אופציונלי.
const BULLET_RE = /^\s*(?:[-*–]\s+|[•●○■▪◦·]\s*)(.*)$/;

/**
 * מפרק טקסט משימה לשורות: שורה רגילה → פסקה (גם שורה ריקה, כדי לשמר את המרווחים
 * הקיימים), ושורות רצופות שמתחילות בסימן בולט → רשימה אחת.
 */
export function parseTextBlocks(text: string | null | undefined): TextBlock[] {
  const blocks: TextBlock[] = [];
  let list: string[] | null = null;
  for (const raw of (text ?? '').split(/\r?\n/)) {
    const line = raw.replace(/\s+$/, '');
    const m = BULLET_RE.exec(line);
    if (m) {
      if (!list) {
        list = [];
        blocks.push({ type: 'ul', items: list });
      }
      list.push(m[1]!.trim());
    } else {
      list = null;
      blocks.push({ type: 'p', text: line });
    }
  }
  return blocks;
}

/**
 * תקציר חד-שורתי של טקסט משימה בלי סימוני עיצוב (**, __, ~~...) — לרשימות ולכרטיסים. בולטים מסומנים ב-•.
 * נחתך ב-max תווים עם "…".
 */
export function plainSnippet(text: string | null | undefined, max = 60): string {
  const plain = (s: string) =>
    parseInline(s)
      .map((r) => r.text)
      .join('')
      .trim();
  const parts: string[] = [];
  for (const block of parseTextBlocks(text)) {
    if (block.type === 'p') {
      const t = plain(block.text);
      if (t) parts.push(t);
    } else {
      for (const item of block.items) {
        const t = plain(item);
        if (t) parts.push(`• ${t}`);
      }
    }
  }
  const joined = parts.join(' ');
  return joined.length > max ? `${joined.slice(0, max - 1).trimEnd()}…` : joined;
}

/** b=מודגש, i=נטוי (עקום), u=קו תחתון, s=קו חוצה, o=קו מעל */
export type InlineStyle = 'b' | 'i' | 'u' | 's' | 'o';

export interface InlineRun {
  text: string;
  styles: InlineStyle[];
}

const STYLE_ORDER: InlineStyle[] = ['b', 'i', 'u', 's', 'o'];

// הארוכים קודם: "**" לפני "*"
const DELIMITERS: { token: string; style: InlineStyle }[] = [
  { token: '**', style: 'b' },
  { token: '__', style: 'u' },
  { token: '~~', style: 's' },
  { token: '^^', style: 'o' },
  { token: '*', style: 'i' },
];

interface DelimToken {
  pos: number;
  len: number;
  style: InlineStyle;
  matched: boolean;
}

const isBlank = (c: string | undefined): boolean => c === undefined || /\s/.test(c);

/**
 * מפרק שורה אחת לקטעי טקסט עם סגנונות. סימון נפתח רק לפני תו שאינו רווח ונסגר רק
 * אחרי תו שאינו רווח, ורק אם יש לו זוג — סימן בודד נשאר טקסט רגיל. סגנונות
 * שונים יכולים לחפוף (כל סגנון הוא דגל עצמאי).
 */
export function parseInline(text: string | null | undefined): InlineRun[] {
  const src = text ?? '';
  const tokens: DelimToken[] = [];
  for (let i = 0; i < src.length; ) {
    const d = DELIMITERS.find((x) => src.startsWith(x.token, i));
    if (d) {
      tokens.push({ pos: i, len: d.token.length, style: d.style, matched: false });
      i += d.token.length;
    } else {
      i++;
    }
  }

  const opener = new Map<InlineStyle, DelimToken>();
  for (const t of tokens) {
    const canClose = !isBlank(src[t.pos - 1]);
    const canOpen = !isBlank(src[t.pos + t.len]);
    const open = opener.get(t.style);
    if (open && canClose && t.pos > open.pos + open.len) {
      open.matched = true;
      t.matched = true;
      opener.delete(t.style);
    } else if (canOpen) {
      opener.set(t.style, t);
    }
  }

  const runs: InlineRun[] = [];
  const active = new Set<InlineStyle>();
  let buf = '';
  const flush = () => {
    if (buf) runs.push({ text: buf, styles: STYLE_ORDER.filter((s) => active.has(s)) });
    buf = '';
  };
  let k = 0;
  for (let i = 0; i < src.length; ) {
    const t = tokens[k];
    if (t && t.pos === i) {
      k++;
      if (t.matched) {
        flush();
        if (active.has(t.style)) active.delete(t.style);
        else active.add(t.style);
      } else {
        buf += src.slice(i, i + t.len);
      }
      i += t.len;
    } else {
      buf += src[i];
      i++;
    }
  }
  flush();
  return runs;
}

/* ───────────── עיצוב מקורי של Google Sheets ↔ סימון הטקסט ─────────────
 * בגיליון מסמנים טקסט ומדגישים/מטים/מקווים כרגיל (Ctrl+B / I / U, קו חוצה). סקריפט ה-Apps Script
 * מעביר את הקטעים המעוצבים, והשרת ממיר אותם לסימון שלמעלה (ובחזרה בכתיבה לגיליון).
 */

/** קטע טקסט עם עיצוב מקורי: מודגש / נטוי / קו תחתון / קו חוצה (קו מעל אינו קיים בשיטס) */
export interface NativeRun {
  t: string;
  b?: boolean | number;
  i?: boolean | number;
  u?: boolean | number;
  s?: boolean | number;
}

type NativeStyle = 'b' | 'i' | 'u' | 's';
const NATIVE_ORDER: NativeStyle[] = ['b', 'i', 'u', 's'];
const NATIVE_CLOSE_ORDER: NativeStyle[] = ['s', 'u', 'i', 'b']; // סגירה בסדר הפוך לפתיחה
const NATIVE_TOKEN: Record<NativeStyle, string> = { b: '**', i: '*', u: '__', s: '~~' };
const NATIVE_BIT: Record<NativeStyle, number> = { b: 1, i: 2, u: 4, s: 8 };
const INLINE_BIT: Record<InlineStyle, number> = { b: 1, i: 2, u: 4, s: 8, o: 16 };

const runMask = (r: NativeRun): number =>
  (r.b ? 1 : 0) | (r.i ? 2 : 0) | (r.u ? 4 : 0) | (r.s ? 8 : 0);

function maskToRun(t: string, mask: number): NativeRun {
  const run: NativeRun = { t };
  for (const s of NATIVE_ORDER) if (mask & NATIVE_BIT[s]) run[s] = true;
  return run;
}

// קידומת בולט בתחילת שורה — העיצוב לעולם לא "תופס" אותה, כדי ששורת הבולט תמשיך להיות מזוהה
const BULLET_PREFIX_RE = /^\s*(?:[-*–]\s+|[•●○■▪◦·]\s*)/;

/** מסכת העיצוב של כל תו, כפי ש-parseInline מפרש את הסימון (לאימות שהסימון שנוצר נקרא נכון) */
function inlineMasks(markupLine: string): { text: string; masks: number[] } {
  let text = '';
  const masks: number[] = [];
  for (const r of parseInline(markupLine)) {
    let m = 0;
    for (const s of r.styles) m |= INLINE_BIT[s];
    for (let k = 0; k < r.text.length; k++) masks.push(m);
    text += r.text;
  }
  return { text, masks };
}

function lineToMarkup(line: string, flags: number[]): string {
  if (flags.every((f) => f === 0)) return line;
  const start = BULLET_PREFIX_RE.exec(line)?.[0].length ?? 0;

  // לכל סגנון: קטעים רצופים, ללא רווחים בקצוות (סימון נפתח/נסגר רק ליד תו שאינו רווח)
  const expected = new Array<number>(line.length).fill(0);
  const opens = new Map<number, NativeStyle[]>();
  const closes = new Map<number, NativeStyle[]>();
  const mark = (map: Map<number, NativeStyle[]>, pos: number, s: NativeStyle) => {
    const list = map.get(pos);
    if (list) list.push(s);
    else map.set(pos, [s]);
  };
  for (const s of NATIVE_ORDER) {
    const bit = NATIVE_BIT[s];
    let p = start;
    while (p < line.length) {
      if (!(flags[p]! & bit)) {
        p++;
        continue;
      }
      let a = p;
      while (p < line.length && flags[p]! & bit) p++;
      let b = p;
      while (a < b && /\s/.test(line[a]!)) a++;
      while (b > a && /\s/.test(line[b - 1]!)) b--;
      if (a === b) continue;
      for (let k = a; k < b; k++) expected[k]! |= bit;
      mark(opens, a, s);
      mark(closes, b, s);
    }
  }

  let markup = '';
  for (let pos = 0; pos <= line.length; pos++) {
    for (const s of NATIVE_CLOSE_ORDER) if (closes.get(pos)?.includes(s)) markup += NATIVE_TOKEN[s];
    for (const s of NATIVE_ORDER) if (opens.get(pos)?.includes(s)) markup += NATIVE_TOKEN[s];
    if (pos < line.length) markup += line[pos];
  }

  // אימות: הסימון שנוצר חייב להיקרא בדיוק כמו העיצוב המקורי (למשל כוכבית אמיתית בתוך הטקסט
  // עלולה להתנגש בסימון). אם לא — השורה נשמרת כטקסט רגיל במקום להציג כוכביות ללקוח.
  const check = inlineMasks(markup);
  if (check.text !== line || check.masks.some((m, k) => m !== expected[k])) return line;
  return markup;
}

/**
 * ממיר קטעי טקסט מעוצבים מהגיליון לסימון הטקסט של האפליקציה (**מודגש**, *נטוי*, __קו תחתון__, ~~קו חוצה~~).
 * העיצוב נשמר לפי שורות (כמו התצוגה): קטע מודגש שחוצה ירידת שורה נסגר ונפתח מחדש בכל שורה.
 */
export function nativeRunsToMarkup(runs: NativeRun[]): string {
  const text = runs.map((r) => r.t).join('');
  if (!runs.some((r) => runMask(r) !== 0)) return text;
  const flags: number[] = [];
  for (const r of runs) {
    const m = runMask(r);
    for (let k = 0; k < r.t.length; k++) flags.push(m);
  }
  const out: string[] = [];
  let offset = 0;
  for (const line of text.split('\n')) {
    out.push(lineToMarkup(line, flags.slice(offset, offset + line.length)));
    offset += line.length + 1;
  }
  return out.join('\n');
}

/**
 * הפעולה ההפוכה — לכתיבה חזרה לגיליון כעיצוב מקורי. מחזיר null כשאין בטקסט עיצוב בכלל, או כשיש
 * בו קו מעל (שאינו קיים בשיטס) — אז הטקסט נכתב כמות שהוא והסימון נשאר גלוי בגיליון.
 */
export function markupToNativeRuns(markup: string): NativeRun[] | null {
  const runs: NativeRun[] = [];
  let styled = false;
  const push = (t: string, mask: number) => {
    if (!t) return;
    const last = runs[runs.length - 1];
    if (last && runMask(last) === mask) last.t += t;
    else runs.push(maskToRun(t, mask));
  };
  const lines = markup.split(/\r?\n/);
  for (let idx = 0; idx < lines.length; idx++) {
    if (idx > 0) push('\n', 0);
    for (const r of parseInline(lines[idx])) {
      if (r.styles.includes('o')) return null;
      let mask = 0;
      for (const s of r.styles) mask |= INLINE_BIT[s];
      if (mask) styled = true;
      push(r.text, mask);
    }
  }
  return styled ? runs : null;
}
